import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { DomainApiError } from '../lib/api-error.js';
import {
  checkDocumentValidity,
  DOCUMENT_CHECK_TYPES,
  type DocumentCheckResultDto,
  type DocumentCheckType,
} from '../lib/person-screening-api.js';

const cs = { locale: 'cs' } as const;

/**
 * Krok NAP8 bod 1 (A18) — KARTA „OVERIT DOKLAD" u osoby / strany smlouvy.
 *
 * Napojuje `InvalidDocumentService` (krok OSB1), ktery byl hotovy, otestovany
 * a BEZ VOLAJICIHO. Makler tu zada cislo dokladu klienta a dostane odpoved
 * Registru neplatnych dokladu MV s DATEM a ZDROJEM.
 *
 * ODKUD SE BERE A KAM TECE:
 *   cislo dokladu   — opise CLOVEK z dokladu; jde do requestu a tim konci
 *                     (`documentNumber` se po dotazu z pole smaze)
 *   vysledek        — odpoved MV; zobrazi se tady, do zakazky jde jen vysledek,
 *                     druh dokladu a posledni ctyri znaky (OSB1 bod 2)
 *   datum overeni   — `queriedOn` ze serveru (serverovy cas, ne cas prohlizece)
 *   zdroj           — `dd.personScreening.documentCheck.source`
 *
 * „NEOVERENO" NENI „PLATNY": `registered === null` se pise jako „nepodarilo
 * se overit" (`state.unknown`), nikdy jako „nic nenalezeno".
 */

/** Stav radku kontroly - tytez tri slova, jaka pouziva `personScreeningRows`. */
function stateKey(result: DocumentCheckResultDto): MessageKey {
  if (result.registered === null) return 'dd.personScreening.state.unknown';
  return result.registered
    ? 'dd.personScreening.state.finding'
    : 'dd.personScreening.state.clear';
}

export function DocumentCheckCard(): React.JSX.Element {
  const [documentType, setDocumentType] = React.useState<DocumentCheckType>('id_card');
  const [documentNumber, setDocumentNumber] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<DocumentCheckResultDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const outcome = await checkDocumentValidity(documentType, documentNumber);
      setResult(outcome);
      // Cislo dokladu v poli uz neni k cemu drzet - vysledek nese jen „…XXXX".
      setDocumentNumber('');
    } catch (cause) {
      setError(
        cause instanceof DomainApiError
          ? cause.message
          : t('dd.personScreening.documentCheck.error', cs),
      );
    } finally {
      setBusy(false);
    }
  }

  /** Hlaska o tvaru cisla dokladu prijde ze serveru jako KLIC, ne jako text. */
  const formatHint =
    result?.formatMessageKey == null
      ? undefined
      : tryT(result.formatMessageKey as MessageKey, cs);

  return (
    <div data-testid="document-check">
      <h4 className="font-medium mt-3">{t('dd.personScreening.documentCheck.heading', cs)}</h4>
      <p className="rh-text-muted">{t('dd.personScreening.documentCheck.notStored', cs)}</p>

      <div className="flex flex-wrap items-center gap-3">
        <label>
          {t('dd.personScreening.documentCheck.typeLabel', cs)}{' '}
          <select
            value={documentType}
            disabled={busy}
            onChange={(event) => setDocumentType(event.target.value as DocumentCheckType)}
            data-testid="document-check-type"
          >
            {DOCUMENT_CHECK_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`dd.personScreening.documentType.${value}` as MessageKey, cs)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('dd.personScreening.documentCheck.numberLabel', cs)}{' '}
          <input
            type="text"
            value={documentNumber}
            disabled={busy}
            // Cislo dokladu nema co delat v historii formularu prohlizece.
            autoComplete="off"
            onChange={(event) => setDocumentNumber(event.target.value)}
            data-testid="document-check-number"
          />
        </label>
        <button
          type="button"
          className="rh-btn"
          disabled={busy || documentNumber.trim() === ''}
          onClick={() => void run()}
          data-testid="document-check-submit"
        >
          {busy
            ? t('dd.personScreening.documentCheck.running', cs)
            : t('dd.personScreening.documentCheck.submit', cs)}
        </button>
      </div>

      {result !== null && (
        <p role="status" data-testid="document-check-result">
          {t('dd.personScreening.check.document_validity', cs)} —{' '}
          <strong>{t(stateKey(result), cs)}</strong>
          {result.documentTail === null
            ? ''
            : ` · ${t('dd.personScreening.documentTail', {
                ...cs,
                values: { tail: result.documentTail },
              })}`}
          {` · ${t('dd.personScreening.checkedOn', { ...cs, values: { date: result.queriedOn } })}`}
        </p>
      )}
      {formatHint !== undefined && (
        <p className="rh-text-warning" data-testid="document-check-format-hint">
          {formatHint}
        </p>
      )}
      {error !== null && (
        <p role="alert" className="rh-a2-error" data-testid="document-check-error">
          {error}
        </p>
      )}
      <p className="rh-text-muted" data-testid="document-check-source">
        {t('dd.personScreening.documentCheck.source', cs)}
      </p>
    </div>
  );
}
